import '../static/Popover.css';
import React__default, { useState, useRef, useCallback, useEffect } from "react";
import clsx from "clsx";
import { createPortal } from "react-dom";
import { u as useClickOutside } from "./useClickOutside.-_RMc0US.mjs";
import { u as useTagName } from "./useTagName.COq0HFfN.mjs";
const popover = `_popover_e3571a3`;
const styles = { popover };
const POPOVER_ID = "ui-popover-portal";
function getPositionCSS({
  popoverEl,
  anchorRef,
  xPosition,
  yPosition,
  autoWidth
}) {
  var _a;
  if (typeof window === "undefined" || !anchorRef.current || !popoverEl) {
    return {};
  }
  const popoverWrapHeight = ((_a = document.getElementById(POPOVER_ID)) == null ? void 0 : _a.clientHeight) || 0;
  const popoverRect = popoverEl.getBoundingClientRect();
  const anchorRect = anchorRef.current.getBoundingClientRect();
  const anchorOffsetTop = anchorRect.top + window.scrollY;
  const anchorOffsetBottom = popoverWrapHeight - anchorRect.bottom - window.scrollY;
  const spaceAbove = anchorRect.top;
  const spaceBelow = window.innerHeight - anchorRect.bottom;
  const spaceLeft = anchorRect.left;
  const spaceRight = window.innerWidth - anchorRect.right;
  const popoverWidth = autoWidth ? popoverRect.width : anchorRect.width;
  const availableXSpace = xPosition === "auto" ? window.innerWidth : xPosition === "right" ? spaceRight : spaceLeft;
  const availableYSpace = yPosition === "center" ? window.innerHeight : yPosition === "top" ? spaceAbove : spaceBelow;
  let posCss = {};
  if (!autoWidth) {
    posCss = { width: popoverWidth };
  }
  if (popoverWidth > availableXSpace) {
    posCss = { ...posCss, width: availableXSpace };
  }
  if (popoverRect.height > availableYSpace) {
    popoverRect.height = availableYSpace;
    posCss = { ...posCss, height: availableYSpace };
  }
  switch (xPosition) {
    case "right":
      posCss = {
        ...posCss,
        left: anchorRect.left + anchorRect.width
      };
      break;
    case "left":
      posCss = {
        ...posCss,
        left: "auto",
        right: anchorRect.right
      };
      break;
    default:
      posCss = {
        ...posCss,
        left: anchorRect.left + anchorRect.width / 2 - popoverWidth / 2
      };
      break;
  }
  switch (yPosition) {
    case "top":
      posCss = {
        ...posCss,
        top: "auto",
        bottom: anchorOffsetBottom + anchorRect.height
      };
      break;
    case "center":
      posCss = {
        ...posCss,
        top: anchorOffsetTop + anchorRect.height / 2 - popoverRect.height / 2
      };
      break;
    default:
      posCss = {
        ...posCss,
        top: anchorOffsetTop + anchorRect.height
      };
      break;
  }
  return posCss;
}
function setArias({
  anchorRef,
  open,
  id,
  role
}) {
  const anchorEl = anchorRef.current;
  if (!anchorEl)
    return;
  switch (role) {
    case "tooltip":
      anchorEl.setAttribute("aria-describedby", id);
      break;
    default:
      anchorEl.setAttribute("aria-haspopup", "true");
      anchorEl.setAttribute("aria-expanded", open ? "true" : "false");
      anchorEl.setAttribute("aria-controls", id);
      break;
  }
}
const PopoverPortal = ({ children }) => {
  const [el, setElement] = useState(null);
  useEffect(() => {
    const id = POPOVER_ID;
    let element = document.getElementById(id);
    function createElement() {
      element = document.createElement("div");
      element.setAttribute("id", id);
      element.setAttribute(
        "style",
        `position: absolute; top: 0; left: 0; z-index: 9999; width: 100%; pointer-events: none;`
      );
      document.body.appendChild(element);
    }
    function resizePortal() {
      if (!element) {
        return;
      }
      element.style.height = "0";
      const scrollHeight = document.documentElement.scrollHeight || document.body.scrollHeight;
      element.style.height = ` ${scrollHeight}px`;
    }
    if (!element) {
      createElement();
    }
    resizePortal();
    setElement(element);
    const observer = new ResizeObserver(() => {
      resizePortal();
    });
    observer.observe(document.body);
    return () => {
      observer.disconnect();
      if (element) {
        element.style.height = "0";
      }
    };
  }, []);
  if (!el || !children) {
    return null;
  }
  return createPortal(children, el);
};
const Popover = ({
  anchorRef,
  as,
  autoWidth = false,
  className,
  closeOnClickOutside = true,
  id = "ui-popover",
  onClose = () => {
  },
  open = false,
  children,
  xPosition = "auto",
  yPosition = "auto",
  role = "tooltip",
  ...props
}) => {
  if (!anchorRef) {
    console.error("ui-corporate: Popover requires an anchor element.");
  }
  const Component = useTagName(as);
  const [popoverEl, setPopoverEl] = useState();
  const [menuCSS, setMenuCSS] = useState({});
  const mousedownFlag = useRef(false);
  const popoverRef = useCallback((node) => {
    if (node !== null) {
      setPopoverEl(node);
    }
  }, []);
  const closePopover = useCallback(() => {
    if (!closeOnClickOutside)
      return;
    onClose();
  }, [onClose]);
  const openPopover = useCallback(() => {
    const anchorEl = anchorRef.current;
    if (!anchorEl)
      return;
    anchorEl.click();
  }, [anchorRef]);
  const handleKeydown = useCallback(
    (event) => {
      if (!children)
        return;
      switch (event.key) {
        case "ArrowUp":
        case "Up":
        case "ArrowDown":
        case "Down":
          event.preventDefault();
          if (!open) {
            openPopover();
          }
          break;
        case "Enter":
        case "Space":
          event.preventDefault();
          if (!open) {
            openPopover();
            break;
          }
          if (role === "tooltip") {
            onClose();
          }
          break;
        case "Escape":
        case "Tab":
          if (open) {
            onClose();
            return;
          }
          break;
      }
    },
    [onClose, openPopover, open]
  );
  useEffect(() => {
    const anchorEl = anchorRef.current;
    if (!anchorEl) {
      return;
    }
    function openPopoverOnFocus() {
      if (role === "tooltip" && !mousedownFlag.current) {
        openPopover();
      }
    }
    function handleMousedown() {
      mousedownFlag.current = true;
    }
    function handleMouseup() {
      mousedownFlag.current = false;
    }
    anchorEl.addEventListener("mousedown", handleMousedown);
    anchorEl.addEventListener("mouseup", handleMouseup);
    anchorEl.addEventListener("keydown", handleKeydown);
    anchorEl.addEventListener("focus", openPopoverOnFocus);
    anchorEl.addEventListener("blur", closePopover);
    return () => {
      anchorEl.removeEventListener("mousedown", handleMousedown);
      anchorEl.removeEventListener("mouseup", handleMouseup);
      anchorEl.removeEventListener("keydown", handleKeydown);
      anchorEl.removeEventListener("focus", openPopoverOnFocus);
      anchorEl.removeEventListener("blur", closePopover);
    };
  }, [anchorRef, handleKeydown, closePopover, openPopover, role]);
  useEffect(() => {
    function calculatePosition() {
      if (!open || !popoverEl)
        return;
      setMenuCSS({});
      setMenuCSS(
        getPositionCSS({
          anchorRef,
          popoverEl,
          xPosition,
          yPosition,
          autoWidth
        })
      );
    }
    calculatePosition();
    window.addEventListener("resize", calculatePosition);
    return () => {
      window.removeEventListener("resize", calculatePosition);
    };
  }, [popoverEl, open]);
  useEffect(() => {
    setArias({ anchorRef, open, id, role });
  }, [anchorRef, open, id, role]);
  useClickOutside(anchorRef, closePopover);
  return /* @__PURE__ */ React__default.createElement(PopoverPortal, null, /* @__PURE__ */ React__default.createElement(
    Component,
    {
      ...props,
      role,
      tabIndex: -1,
      "data-open": open && Object.values(menuCSS).length > 0,
      ref: popoverRef,
      className: clsx(styles.popover, className),
      style: menuCSS
    },
    children
  ));
};
export {
  Popover as P
};
//# sourceMappingURL=Popover.DkgLMtxz.mjs.map
